import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { competitionAPI } from '../api/endpoints';
import { FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreateCompetition.css';

const CreateCompetition = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    visibility: 'public',
    is_free: true,
    join_price: 0,
    joining_code: '',
    total_winners: 1,
    prize_percentage: 70,
  });

  const isPunterPlus = user?.punter_plus || false;

  useEffect(() => {
    if (!isPunterPlus) {
      toast.error('You need Punter+ to create competitions');
      navigate('/subscription');
    }
  }, [isPunterPlus, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await competitionAPI.create(formData);
      toast.success('Competition created successfully!');
      navigate(`/competitions/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create competition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-competition-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="create-competition-card">
        <div className="card-header">
          <h1>Create Competition</h1>
          <p className="card-subtitle">Set up a new prediction competition</p>
          {isPunterPlus && (
            <span className="punter-badge">⭐ Punter+</span>
          )}
        </div>

        <form onSubmit={handleSubmit} className="create-competition-form">
          {/* Title */}
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              placeholder="e.g., Premier League Predictions"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              placeholder="Describe your competition..."
              value={formData.description}
              onChange={handleChange}
              rows={4}
            />
          </div>

          {/* Dates */}
          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="datetime-local"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="datetime-local"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Visibility & Pricing */}
          <div className="form-row">
            <div className="form-group">
              <label>Visibility</label>
              <select
                name="visibility"
                value={formData.visibility}
                onChange={handleChange}
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div className="form-group">
              <label>Joining Code (for private)</label>
              <input
                type="text"
                name="joining_code"
                placeholder="Enter joining code"
                value={formData.joining_code}
                onChange={handleChange}
                disabled={formData.visibility === 'public'}
              />
            </div>
          </div>

          {/* Free Entry */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_free"
                checked={formData.is_free}
                onChange={handleChange}
              />
              <span>Free Entry</span>
            </label>
          </div>

          {!formData.is_free && (
            <div className="form-group">
              <label>Entry Price ($XPUNT)</label>
              <input
                type="number"
                name="join_price"
                placeholder="e.g., 5.00"
                value={formData.join_price}
                onChange={handleChange}
                step="0.01"
                min="0.01"
              />
            </div>
          )}

          {/* Winners & Prize */}
          <div className="form-row">
            <div className="form-group">
              <label>Total Winners</label>
              <input
                type="number"
                name="total_winners"
                value={formData.total_winners}
                onChange={handleChange}
                min="1"
                max="100"
              />
            </div>
            <div className="form-group">
              <label>Prize Percentage (%)</label>
              <input
                type="number"
                name="prize_percentage"
                value={formData.prize_percentage}
                onChange={handleChange}
                min="1"
                max="100"
              />
              <small className="hint">Percentage of pool distributed to winners</small>
            </div>
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'Create Competition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCompetition;